import { createFileRoute, Link as RouterLink } from "@tanstack/react-router";
import { Pencil, UserPlus } from "lucide-react";
import {
  Button,
  PageHeader,
  Skeleton,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  compareNames,
  ICON_STROKE,
} from "@optymyse/suite-ui";
import { edits } from "../../shared/schemas/workspaces";
import { useCompany, useCompanyContacts } from "../lib/directory";
import { addressLines } from "../lib/format";
import { workspaceSlug } from "../lib/hosts";
import { useWorkspace } from "../lib/workspaces";
import { fill, strings } from "../lib/strings";
import { ApiError } from "../lib/api";
import { DirectoryProblem, None, Page, Panel, Properties, Property } from "../components/directory/Page";
import { History } from "../components/directory/History";

/**
 * A customer: its names, domain, website, address and notes as Link holds them now, its contacts, and what happened to
 * it, newest first, each read live. An Editor or an Administrator edits it.
 */
function CustomerPage() {
  const { companyId } = Route.useParams();
  const view = useWorkspace(workspaceSlug());
  const company = useCompany(companyId);
  const mayEdit = view.data ? edits(view.data.role) : false;
  const breadcrumb = [{ label: strings.customers.title, href: "/admin/customers" }];

  if (company.isPending)
    return (
      <Page>
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48" />
      </Page>
    );
  if (company.isError) {
    const refusal = company.error instanceof ApiError ? company.error : null;
    const gone = refusal !== null && (refusal.status === 404 || refusal.status === 410);
    return (
      <Page>
        <PageHeader breadcrumb={[...breadcrumb, { label: strings.customers.title }]} title={strings.customers.title} />
        {gone ? (
          <p>{refusal?.message || strings.customers.page.gone}</p>
        ) : (
          <DirectoryProblem {...(refusal ? { message: refusal.message } : {})} onRetry={() => void company.refetch()} />
        )}
      </Page>
    );
  }

  const record = company.data;
  const address = addressLines(record.address);
  return (
    <Page>
      <PageHeader
        breadcrumb={[...breadcrumb, { label: record.display_name }]}
        title={record.display_name}
        {...(record.domain ? { description: record.domain } : {})}
        actions={
          mayEdit ? (
            <Button asChild variant="secondary">
              <RouterLink to="/admin/customers/$companyId/edit" params={{ companyId }}>
                <Pencil aria-hidden strokeWidth={ICON_STROKE} />
                {strings.customers.page.edit}
              </RouterLink>
            </Button>
          ) : undefined
        }
      />
      <Panel title={strings.customers.page.details}>
        <Properties>
          <Property label={strings.customers.page.displayName}>{record.display_name}</Property>
          <Property label={strings.customers.page.legalName}>{record.legal_name ?? <None />}</Property>
          <Property label={strings.customers.page.domain}>{record.domain ?? <None />}</Property>
          <Property label={strings.customers.page.website}>
            {record.website && /^https?:\/\//i.test(record.website) ? (
              <a
                href={record.website}
                target="_blank"
                rel="noreferrer noopener"
                className="text-accent-text underline-offset-2 hover:underline"
              >
                {record.website}
              </a>
            ) : record.website ? (
              // Only a web address is a link: anything else Link holds is shown as written.
              <span className="break-all">{record.website}</span>
            ) : (
              <None />
            )}
          </Property>
          <Property label={strings.customers.page.address}>
            {address.length > 0 ? (
              <address className="not-italic" aria-label={strings.customers.page.address}>
                {address.map((line, index) => (
                  <span key={index} className="block">
                    {line}
                  </span>
                ))}
              </address>
            ) : (
              <None />
            )}
          </Property>
        </Properties>
      </Panel>
      <Panel title={strings.customers.page.notes}>
        {record.notes ? (
          <p data-testid="customer-notes" className="break-words whitespace-pre-wrap">
            {record.notes}
          </p>
        ) : (
          <None />
        )}
      </Panel>
      <CustomerContacts companyId={companyId} mayEdit={mayEdit} />
      <History kind="company" id={companyId} />
    </Page>
  );
}

/** The customer's contacts, A to Z, each opening on their own page. */
function CustomerContacts({ companyId, mayEdit }: { companyId: string; mayEdit: boolean }) {
  const contacts = useCompanyContacts(companyId);
  const rows = [...(contacts.data?.items ?? [])].sort((a, b) => compareNames(a.display_name, b.display_name));
  return (
    <Panel
      title={strings.customers.page.contacts}
      aside={
        mayEdit ? (
          <Button asChild variant="secondary" size="sm">
            <RouterLink to="/admin/contacts/new" search={{ company: companyId }}>
              <UserPlus aria-hidden strokeWidth={ICON_STROKE} />
              {strings.customers.page.addContact}
            </RouterLink>
          </Button>
        ) : undefined
      }
    >
      {contacts.isPending ? (
        <Skeleton className="h-16" />
      ) : contacts.isError ? (
        <DirectoryProblem
          {...(contacts.error instanceof ApiError ? { message: contacts.error.message } : {})}
          onRetry={() => void contacts.refetch()}
        />
      ) : rows.length === 0 ? (
        <p className="text-ink-muted">{strings.customers.page.noContacts}</p>
      ) : (
        <>
          {contacts.data?.capped && (
            <p className="mb-2 text-secondary text-ink-muted">
              {fill(strings.directory.capped, { cap: contacts.data.cap })}
            </p>
          )}
          <Table containerClassName="shadow-none">
            <TableCaption>{strings.customers.page.contacts}</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead>{strings.contacts.columns.name}</TableHead>
                <TableHead>{strings.contacts.columns.email}</TableHead>
                <TableHead>{strings.contacts.columns.phone}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((contact) => (
                <TableRow key={contact.id}>
                  <TableCell>
                    <RouterLink
                      to="/admin/contacts/$contactId"
                      params={{ contactId: contact.id }}
                      className="font-medium text-ink underline-offset-2 hover:underline"
                    >
                      {contact.display_name}
                    </RouterLink>
                  </TableCell>
                  <TableCell className="text-ink-muted">{contact.primary_email ?? ""}</TableCell>
                  <TableCell className="text-ink-muted">{contact.primary_phone ?? ""}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
    </Panel>
  );
}

export const Route = createFileRoute("/admin/customers/$companyId/")({ component: CustomerPage });
